import { ChefHat, Sparkles, PawPrint } from "lucide-react";
import Reveal from "@/components/Reveal";
import { FAZZENDA_MEDIA } from "@/data/resorts";

const MEALS = ["Café colonial da manhã", "Almoço típico de fazenda", "Café da tarde com cucas", "Jantar completo à noite"];

function ImageCard({ src, alt, label, sub, className = "", testid }) {
    return (
        <div className={`group relative overflow-hidden rounded-3xl ${className}`} data-testid={testid}>
            <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
                <span className="inline-block rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold tracking-wide text-white backdrop-blur-md">
                    {label}
                </span>
                {sub && <p className="mt-2 text-xs text-stone-200">{sub}</p>}
            </div>
        </div>
    );
}

export default function Experience() {
    return (
        <section id="experiencia" className="bg-sand py-24 sm:py-32" data-testid="experience-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-hover">A Experiência Fazzenda</p>
                    <h2 className="mt-3 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl lg:text-5xl">
                        Muito além de uma hospedagem
                    </h2>
                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-stone-600">
                        Um resort de fazenda completo no coração do Vale Europeu: do café colonial ao pôr do sol a cavalo,
                        cada dia aqui é desenhado para virar memória.
                    </p>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
                    <Reveal className="md:col-span-7">
                        <ImageCard
                            src={FAZZENDA_MEDIA.pools}
                            alt="Complexo de piscinas aquecidas do resort"
                            label="Complexo de Piscinas & Hidromassagens"
                            sub="Mais de 3.000 m² de piscinas aquecidas integradas à paisagem natural"
                            className="h-[380px] md:h-[520px]"
                            testid="experience-card-pools"
                        />
                    </Reveal>

                    <Reveal className="md:col-span-5" delay={0.12}>
                        <div className="flex h-[380px] flex-col justify-between rounded-3xl bg-forest-deep p-8 md:h-[520px]" data-testid="experience-card-gastronomy">
                            <div>
                                <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-4 py-2 text-xs font-semibold tracking-wide text-gold">
                                    <ChefHat className="h-4 w-4" />
                                    Pensão Completa
                                </span>
                                <h3 className="mt-5 font-serif text-2xl font-semibold text-white sm:text-3xl">
                                    Gastronomia de fazenda, do amanhecer ao anoitecer
                                </h3>
                            </div>
                            <ul className="space-y-3">
                                {MEALS.map((m) => (
                                    <li key={m} className="flex items-center gap-3 text-sm text-stone-200">
                                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                                        {m}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs font-medium uppercase tracking-[0.18em] text-gold/80">Tudo incluso na diária</p>
                        </div>
                    </Reveal>

                    <Reveal className="md:col-span-4" delay={0.05}>
                        <ImageCard
                            src={FAZZENDA_MEDIA.cavalgada}
                            alt="Cavalgada guiada nos campos do resort"
                            label="Cavalgadas & Charretes"
                            className="h-[340px]"
                            testid="experience-card-horses"
                        />
                    </Reveal>

                    <Reveal className="md:col-span-4" delay={0.15}>
                        <div className="flex h-[340px] flex-col justify-between rounded-3xl bg-gold p-8" data-testid="experience-card-recreation">
                            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest-deep/10 px-4 py-2 text-xs font-semibold tracking-wide text-forest-deep">
                                <Sparkles className="h-4 w-4" />
                                Recreação Monitorada
                            </span>
                            <div>
                                <h3 className="font-serif text-2xl font-semibold text-forest-deep">Diversão o dia todo, com a Turma do Gasparinho</h3>
                                <p className="mt-3 text-sm leading-relaxed text-forest-deep/80">
                                    Equipe de recreadores, mascotes exclusivos, atividades kids e brincadeiras de fazenda —
                                    as crianças se divertem e os pais relaxam de verdade.
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal className="md:col-span-4" delay={0.25}>
                        <div className="group relative h-[340px] overflow-hidden rounded-3xl" data-testid="experience-card-minifarm">
                            <img src={FAZZENDA_MEDIA.animais} alt="Crianças com os porquinhos da mini fazenda" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2">
                                <PawPrint className="h-4 w-4 text-gold" />
                                <span className="text-xs font-semibold tracking-wide text-white">Mini Fazenda & Contato com Animais</span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
